import React from "react";
import { Target, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const faqsLeft = [
  {
    title: "What is vector database migration?",
    desc: "Vector database migration is the process of transferring vector embeddings, metadata, and configurations from one vector database to another. This enables organisations to switch platforms, optimise costs, or leverage better features without losing their existing AI and machine learning infrastructure.",
  },
  {
    title: "Which vector databases do you support?",
    desc: "We support all major vector databases including Pinecone, Qdrant, Weaviate, Chroma, Milvus, and more. Our platform handles seamless migration between any combination of these databases while preserving data integrity and search performance.",
  },
  {
    title: "Is my data secure during migration?",
    desc: "Yes. All data transfers use encrypted connections (TLS 1.3). We never store your API credentials or vector data permanently. Migrations occur through secure, direct connections between your source and target databases with zero intermediate storage.",
  },
  {
    title: "Can I migrate without downtime?",
    desc: "Absolutely. Our zero-downtime migration strategy keeps your existing database operational while syncing data to the new platform. You can switch over when ready, with rollback options available if needed.",
  },
  {
    title: "What happens to my metadata and filters?",
    desc: "All metadata fields, filtering configurations, and indexes are preserved during migration. We automatically map field types and structures to match your target database's schema requirements while maintaining search accuracy.",
  },
];

const faqsRight = [
  {
    title: "Do I need technical expertise to migrate?",
    desc: "No coding required. Our user-friendly interface guides you through the entire process with simple configuration steps. For complex migrations, our technical support team provides hands-on assistance and custom solutions.",
  },
  {
    title: "How much does vector migration cost?",
    desc: "Pricing is based on the number of vectors migrated, starting at $X per million vectors. We offer transparent pricing with no hidden fees, and provide a free cost calculator to estimate your migration expenses before starting.",
  },
  {
    title: "Will migration affect my search performance?",
    desc: "No. We preserve vector dimensions, similarity metrics, and indexing strategies to maintain identical search results. Post-migration validation ensures query performance matches or exceeds your original database configuration.",
  },
  {
    title: "Can I test the migration before going live?",
    desc: "Yes. We offer sandbox testing where you can migrate a sample of your data to verify accuracy and performance. This risk-free testing ensures complete confidence before committing to full-scale migration.",
  },
  {
    title: "Do you offer post-migration support?",
    desc: "Yes. We provide 30 days of free post-migration support including performance optimisation, query tuning, and troubleshooting. Extended support plans and consulting services are available for ongoing assistance.",
  },
];

const FAQItem = ({ title, desc }) => (
  <div className="relative pl-14 pb-12">
    {/* Icon Circle */}
    <div className="absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center shadow-md bg-primary">
      <Target className="w-5 h-5 text-white" />
    </div>

    {/* Vertical connector line */}
    <div
      className="absolute left-5 top-10 w-px h-full"
      style={{ background: 'linear-gradient(to bottom, #CBD5E1, transparent)' }}
    />

    <h3 className="font-semibold mb-3 text-base leading-snug text-text-primary">
      {title}
    </h3>
    <p className="text-sm leading-relaxed text-text-secondary">
      {desc}
    </p>
  </div>
);

export default function FQAPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section id="faq" className="py-10 mt-10 px-6">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="text-center mt-10 mb-16">
            <h1 className="text-4xl font-bold mb-4 text-text-primary">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-text-secondary">
              Everything you need to know about vector database migration
            </p>
          </div>

          {/* FAQ Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
            <div>
              {faqsLeft.map((f, i) => (
                <FAQItem key={i} {...f} />
              ))}
            </div>
            <div>
              {faqsRight.map((f, i) => (
                <FAQItem key={i} {...f} />
              ))}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}